'use client';

import { useEffect, useRef, type RefObject } from 'react';

const focusableSelector = 'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

/** 仅最上层对话框接管焦点；内嵌选择器关闭后恢复到原触发控件。 */
export function useDialogFocus(ref: RefObject<HTMLElement | null>, active: boolean, onEscape?: () => void) {
  const escapeRef = useRef(onEscape);
  escapeRef.current = onEscape;

  useEffect(() => {
    const dialog = ref.current;
    if (!active || !dialog) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const isTopmost = () => Array.from(document.querySelectorAll('[role="dialog"][aria-modal="true"]'))
      .filter((element) => element.getClientRects().length > 0).at(-1) === dialog;
    const controls = () => Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector))
      .filter((element) => element.tabIndex >= 0 && element.getClientRects().length > 0 && !element.closest('[inert], [aria-hidden="true"]'));
    const focusFirst = () => (controls()[0] ?? dialog).focus({ preventScroll: true });
    const frame = requestAnimationFrame(() => { if (isTopmost()) focusFirst(); });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || !isTopmost()) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        escapeRef.current?.();
      } else if (event.key === 'Tab') {
        const elements = controls();
        const first = elements[0];
        const last = elements.at(-1);
        if (!first || !dialog.contains(document.activeElement)) {
          event.preventDefault();
          (event.shiftKey ? last ?? dialog : first ?? dialog).focus();
        } else if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    const onFocusIn = (event: FocusEvent) => {
      if (isTopmost() && event.target instanceof Node && !dialog.contains(event.target)) focusFirst();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn);
      if (previous?.isConnected) previous.focus({ preventScroll: true });
    };
  }, [active, ref]);
}
